import React from "react";

import classNames from "classnames";
import { FieldValues, Path, UseFormReturn } from "react-hook-form";

export type InputTextField = React.ComponentPropsWithoutRef<"input">;
type InputTextAreaField = React.ComponentPropsWithoutRef<"textarea">;
type FormInputProps<T extends FieldValues> = {
  name: Path<T>;
  formProps: Pick<UseFormReturn<T>, "register" | "trigger" | "getFieldState">;
};
export const TextInput = <T extends FieldValues>({
  formProps: { register, trigger, getFieldState },
  onChange,
  className,
  ...props
}: InputTextField & FormInputProps<T>) => {
  const error = getFieldState(props.name).error;
  return (
    <input
      className={classNames(
        "block w-full appearance-none rounded border-2 border-neutral-200 bg-white px-16 py-8 leading-normal focus:outline-none focus:ring",
        "disabled:bg-neutral-100 disabled:text-neutral-400",
        { "border-red-500": error },
        className,
      )}
      id={props.name}
      {...props}
      {...register(props.name, {
        onChange: (e) => {
          trigger(props.name);
          onChange && onChange(e);
        },
      })}
    />
  );
};

export const TextAreaInput = <T extends FieldValues>({
  formProps: { register, trigger, getFieldState },
  onChange,
  className,
  ...props
}: InputTextAreaField & FormInputProps<T>) => {
  const error = getFieldState(props.name).error;
  return (
    <textarea
      className={classNames(
        "block w-full appearance-none rounded border-2 border-primary-100 bg-white px-16 py-8 leading-normal focus:outline-none focus:ring",
        { "border-red-500": error },
        className,
      )}
      {...register(props.name, {
        onChange: (e) => {
          trigger(props.name);
          onChange && onChange(e);
        },
      })}
      {...props}
    />
  );
};

export const StyledStandAloneInput = (props: InputTextField) => {
  return (
    <input
      className="block w-full appearance-none rounded border-2 border-neutral-200 bg-white px-16 py-8 leading-normal focus:outline-none focus:ring"
      {...props}
    />
  );
};
/*
 * This should only be used as your very last resort.
 *
 * This component lets us keep all the react-hook-form stuff
 * But without the styling to allowing for custom styling when
 * The normal styling is not appropiate and can not be overridden
 *
 */
export const UnstyledTextInput = <T extends FieldValues>({
  formProps: { register, trigger },
  onChange,
  className,
  name,
  ...props
}: InputTextField & FormInputProps<T>) => {
  return (
    <input
      className={className}
      {...register(name, {
        onChange: (e) => {
          trigger(name);
          onChange && onChange(e);
        },
      })}
      {...props}
    />
  );
};
export const UnstyledTextAreaInput = <T extends FieldValues>({
  formProps: { register, trigger },
  onChange,
  name,
  className,
  ...props
}: InputTextAreaField & FormInputProps<T>) => {
  return (
    <textarea
      className={classNames(
        "block w-full appearance-none bg-white leading-normal focus:outline-none focus:ring",
        className,
      )}
      {...register(name, {
        onChange: (e) => {
          trigger(name);
          onChange && onChange(e);
        },
      })}
      {...props}
    />
  );
};
